import React, { useEffect, useRef, useState } from 'react';
import '../styles/selectedWork.scss';

type ArtifactKey = 'before' | 'flow' | 'ui';

const RAIL: { key: ArtifactKey; label: string }[] = [
    { key: 'before', label: 'Before — the old way' },
    { key: 'flow', label: 'Redesigned flow' },
    { key: 'ui', label: 'Product UI' },
];

const DEFAULT_KEY: ArtifactKey = 'flow';

const RED = '#b0424a';

// tiny wrapper so every stroke icon shares the same svg attrs
const Icon: React.FC<{
    size?: number;
    stroke?: string;
    width?: number;
    children: React.ReactNode;
}> = ({ size = 24, stroke = 'currentColor', width = 1.9, children }) => (
    <svg
        width={size}
        height={size}
        viewBox='0 0 24 24'
        fill='none'
        stroke={stroke}
        strokeWidth={width}
        strokeLinecap='round'
        strokeLinejoin='round'
        aria-hidden='true'
    >
        {children}
    </svg>
);

// BEFORE: the spreadsheet-and-email pain points. each card tilts a hair, like loose paper
const PAINS: { title: string; desc: string; rot: number; icon: React.ReactNode }[] = [
    {
        title: 'Local Excel files',
        desc: 'Versions live on individual machines',
        rot: -1.2,
        icon: (
            <Icon size={26} stroke={RED}>
                <path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' />
                <path d='M14 2v6h6' />
                <path d='m9.5 12.5 5 5' />
                <path d='m14.5 12.5-5 5' />
            </Icon>
        ),
    },
    {
        title: 'Email handoffs',
        desc: 'Approvals buried in inboxes',
        rot: 1,
        icon: (
            <Icon size={26} stroke={RED}>
                <rect x='2' y='4' width='20' height='16' rx='2' />
                <path d='m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7' />
            </Icon>
        ),
    },
    {
        title: 'Inconsistent doc tracking',
        desc: 'No shared source of truth',
        rot: 0.8,
        icon: (
            <Icon size={26} stroke={RED}>
                <path d='M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z' />
                <path d='M12 10v4' />
                <path d='M12 17h.01' />
            </Icon>
        ),
    },
    {
        title: 'Manual reporting',
        desc: 'Leadership visibility by hand',
        rot: -0.9,
        icon: (
            <Icon size={26} stroke={RED}>
                <path d='M3 3v16a2 2 0 0 0 2 2h16' />
                <rect x='7' y='13' width='3' height='5' />
                <rect x='12' y='9' width='3' height='9' />
                <rect x='17' y='5' width='3' height='13' />
            </Icon>
        ),
    },
];

// FLOW: the six stages of the new intake-to-closeout workflow
const STEPS: { n: number; title: string; desc: string; icon: React.ReactNode }[] = [
    {
        n: 1,
        title: 'Project Intake',
        desc: 'Project details, stakeholders, lease context',
        icon: (
            <Icon size={17}>
                <path d='M15 2H9a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1Z' />
                <path d='M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2' />
                <path d='m9 14 2 2 4-4' />
            </Icon>
        ),
    },
    {
        n: 2,
        title: 'Scope & Objectives',
        desc: 'Stakeholder alignment',
        icon: (
            <Icon size={17}>
                <circle cx='12' cy='12' r='9' />
                <circle cx='12' cy='12' r='5' />
                <circle cx='12' cy='12' r='1' />
            </Icon>
        ),
    },
    {
        n: 3,
        title: 'Budget & Due Diligence',
        desc: 'Required documents and budget inputs',
        icon: (
            <Icon size={17}>
                <rect x='4' y='2' width='16' height='20' rx='2' />
                <path d='M8 6h8' />
                <path d='M8 10h2' />
                <path d='M14 10h2' />
                <path d='M8 14h2' />
                <path d='M14 14h2' />
                <path d='M8 18h2' />
                <path d='M14 18h2' />
            </Icon>
        ),
    },
    {
        n: 4,
        title: 'Design, Bidding & Approvals',
        desc: 'Proposal routing and review',
        icon: (
            <Icon size={17}>
                <path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' />
                <path d='M14 2v6h6' />
                <circle cx='11.5' cy='14.5' r='2.5' />
                <path d='m13.5 16.5 2 2' />
            </Icon>
        ),
    },
    {
        n: 5,
        title: 'Contracts, Insurance & Construction',
        desc: 'Work can begin',
        icon: (
            <Icon size={17}>
                <path d='M12 2 3 7v13h6v-6h6v6h6V7Z' />
                <path d='M12 2v5' />
            </Icon>
        ),
    },
    {
        n: 6,
        title: 'Closeout & Handoff',
        desc: 'Progress tracking and final transition',
        icon: (
            <Icon size={17}>
                <path d='m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z' />
                <path d='m9 12 2 2 4-4' />
            </Icon>
        ),
    },
];

const PanelHead: React.FC<{ label: string; tone: 'red' | 'teal' }> = ({ label, tone }) => (
    <div className='work-phead'>
        <span className={`work-plabel work-plabel--${tone}`}>{label}</span>
        <span className={`work-prule work-prule--${tone}`} />
    </div>
);

const BeforePanel: React.FC = () => (
    <>
        <PanelHead label='The old way' tone='red' />
        <div className='work-before-grid'>
            {PAINS.map((p) => (
                <div
                    key={p.title}
                    className='work-pain'
                    style={{ transform: `rotate(${p.rot}deg)` }}
                >
                    {p.icon}
                    <h4>{p.title}</h4>
                    <p>{p.desc}</p>
                </div>
            ))}
        </div>
        <div className='work-before-foot'>
            <Icon size={17} stroke={RED} width={2}>
                <path d='m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z' />
                <path d='M12 9v4' />
                <path d='M12 17h.01' />
            </Icon>
            Slow, error-prone handoffs, and no one place to see project status.
        </div>
    </>
);

const FlowPanel: React.FC = () => (
    <>
        <PanelHead label='One centralized workflow' tone='teal' />
        <div className='work-flow'>
            <div className='work-flow-line' />
            {STEPS.map((s) => (
                <div key={s.n} className='work-step'>
                    <div className='work-step-n'>{s.n}</div>
                    <div className='work-step-ic'>{s.icon}</div>
                    <div>
                        <h4>{s.title}</h4>
                        <p>{s.desc}</p>
                    </div>
                </div>
            ))}
        </div>
    </>
);

// the demo is a standalone static app in public/demo, so it lives in an iframe
// (keeps its jQuery/bootstrap and #hash routing out of my way)
const DEMO_URL = `${process.env.PUBLIC_URL}/demo/index.html`;

const UiPanel: React.FC = () => (
    <>
        <div className='work-phead'>
            <span className='work-plabel work-plabel--teal'>Product UI — live interactive demo</span>
            <span className='work-prule work-prule--teal' />
            <a className='work-demo-open' href={DEMO_URL} target='_blank' rel='noreferrer'>
                Open full screen
                <Icon size={12} width={2}>
                    <path d='M7 17 17 7' />
                    <path d='M8 7h9v9' />
                </Icon>
            </a>
        </div>
        <div className='work-shot-frame'>
            <iframe
                className='work-demo'
                src={DEMO_URL}
                title='Construction Operations Platform — interactive product UI demo'
                loading='lazy'
            />
        </div>
    </>
);

// ⬡ stop 2: selected work — the construction ops platform, before / after / live demo.
const SelectedWork: React.FC = () => {
    const [activeKey, setActiveKey] = useState<ArtifactKey>(DEFAULT_KEY);

    // hidden until you scroll close, so the title doesn't peek on page load.
    // the -120px bottom margin stops a half-visible title from triggering it
    const containerRef = useRef<HTMLDivElement>(null);
    const [revealed, setRevealed] = useState(false);
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const io = new IntersectionObserver(
            ([entry], obs) => {
                if (entry.isIntersecting) {
                    setRevealed(true);
                    obs.disconnect();
                }
            },
            { threshold: 0.15, rootMargin: '0px 0px -120px 0px' },
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    return (
        <div ref={containerRef} className={`selectedWork-container${revealed ? ' is-revealed' : ''}`}>
            <header className='selectedWork-header'>Selected Work</header>
            <h3 className='work-title'>Construction Operations Platform</h3>
            <p className='work-subtitle'>
                From scattered spreadsheets &amp; email to one centralized workflow
            </p>
            <p className='work-role'>
                Sole developer · ASP.NET Core · SQL Server · Entity Framework · discovery through rollout
            </p>

            <div className='work-grid'>
                {/* keyed panel, so the fade replays on every swap */}
                <div className='work-stage' aria-live='polite'>
                    <div key={activeKey} className='work-panel'>
                        {activeKey === 'before' && <BeforePanel />}
                        {activeKey === 'flow' && <FlowPanel />}
                        {activeKey === 'ui' && <UiPanel />}
                    </div>
                </div>

                <div className='work-rail'>
                    {RAIL.map((a) => (
                        <button
                            key={a.key}
                            type='button'
                            data-key={a.key}
                            className={`work-thumb${a.key === activeKey ? ' is-active' : ''}`}
                            aria-pressed={a.key === activeKey}
                            onClick={() => setActiveKey(a.key)}
                        >
                            <span className='work-thumb-dot' />
                            <span className='work-thumb-name'>{a.label}</span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SelectedWork;
